import { useQuery } from "@tanstack/react-query";
import useWidgetStore from "../../store/useWidgetStore";
import ConfigHolder from "../../config/ConfigHolder";
import { prepareUrl } from "../../context/WidgetContext";
import { useSettings } from "../../context/SettingsContext";
import dayjs from 'dayjs';
/**
 * getDates answers for one calendar month at a time, and which month that is normally follows
 * from selectedDate. That is all a month calendar ever needs, but a week view straddles month
 * boundaries (Mon 31 Aug - Sun 6 Sep), so it also asks for the neighbouring month through
 * useDatesForMonth and WidgetContext merges the two into the dates store.
 *
 * `pinned` only says where the month comes from: the pinned variant is handed one and stays idle
 * until it has one, the plain variant reads it off selectedDate.
 *
 * Both key on the MONTH, never on selectedDate. The request only varies by month, so keying on
 * the day meant every click refetched the month it was already showing - and worse, in a week
 * view it broke the picker outright. Picking a day in the other half of a week swaps which month
 * is "primary" and which is "extra"; with day-based keys both queries missed the cache at that
 * moment, the store lost the month the selection was in, WeekPicker moved the selection to a day
 * of the month that was still loaded, and that swapped the two months back. The two effects then
 * chased each other, two requests per turn, and the week never rendered. Keyed on the month the
 * two hooks share one cache entry per month, so a swap is a cache hit and there is nothing to
 * oscillate.
 */
const useDatesQuery = (month, pinned, options) => {
    const { enabled, selectedApptype, selectedResource, selectedDate, selectedGroup, selectedCapacity, selectedCapacityGroups } = useWidgetStore();
    const { api_key, widget_id, displayNoCapacity } = useSettings();
    // the month this query is for: the one it is pinned to, or the one selectedDate falls in
    const targetMonth = month !== null && month !== void 0 ? month : (selectedDate && selectedDate[0] !== undefined
        ? dayjs(selectedDate[0]).format('YYYYMM')
        : dayjs().format('YYYYMM'));
    const defaultOptions = Object.assign(Object.assign({}, options), { enabled: (pinned ? !!month : true)
            && enabled.date && selectedCapacity > 0 && selectedResource && selectedResource[0] !== undefined, networkMode: "online", cacheTime: 60 * 1000 * 2, staleTime: 60 * 1000 * 2 });
    return useQuery(Object.assign(Object.assign({}, defaultOptions), { 
        // every parameter the request varies by is in here, and nothing that it does not:
        // selectedGroup travels along as `category`, so it belongs in the key as well
        queryKey: ['widget-date', selectedApptype, selectedResource, selectedGroup,
            pinned && !month ? 'idle' : targetMonth, selectedCapacity, selectedCapacityGroups], queryFn: async ({ signal }) => {
            let url = new URL(`/consumer/booking/api/method/getDates`, ConfigHolder.apiBaseUrl);
            let capacities = JSON.stringify(selectedCapacityGroups);
            if (selectedCapacityGroups[0]) {
                // if group 0 is "set", make sure it represent the overall capacity
                capacities = JSON.stringify({ 0: selectedCapacity });
            }
            let params = {
                filter_apptype: '',
                filter_resource: '',
                apptypes: selectedApptype && selectedApptype[0] !== undefined ? selectedApptype === null || selectedApptype === void 0 ? void 0 : selectedApptype.join(',') : -1,
                resource: selectedResource && selectedResource[0] !== undefined ? selectedResource === null || selectedResource === void 0 ? void 0 : selectedResource.join(',') : "",
                category: selectedGroup && selectedGroup[0] !== undefined ? selectedGroup.join(',') : -1,
                capacity: selectedCapacity,
                capacities: capacities,
                month: targetMonth,
                first: 1
            };
            if (displayNoCapacity) {
                params.include = 'capacity';
            }
            url = prepareUrl(url, 'getDates', api_key, widget_id, params);
            const response = await fetch(url.href, {
                signal: signal,
                headers: {
                    'accept': 'application/ld+json',
                }
            });
            return response.json();
        } }));
};
export const useDates = (options) => useDatesQuery(undefined, false, options);
/** The second month a week view needs when its week spans two. Idle while `month` is undefined. */
export const useDatesForMonth = (month, options) => useDatesQuery(month, true, options);
